/* Hallmark · macrostructure: Bento Grid · tone: utilitarian · anchor hue: steel-blue ~215°
 * genre: modern-minimal · theme: Cobalt-adapted (project palette)
 * pre-emit critique: P5 H5 E5 S5 R5 V4
 */
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Database, Server, Zap, Weight, LayoutGrid, AlertCircle, Map } from 'lucide-react';
import { formatRackName } from '../utils/rackName.logic';

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface RackConfig {
  id: number;
  row_index: number;
  rack_index: number;
  x_m: number | null;
  y_m: number | null;
  rotation: number;
  type: string;
  height_m: number | null;
  u_slots: number | null;
  max_weight_kg?: number;
  max_power_w?: number;
  is_blocked: boolean;
  is_facility?: boolean;
  name?: string;
  label?: string;
}

/* ─── Stat chip ──────────────────────────────────────────────────────────────── */

function StatChip({ icon: Icon, value, unit, dim }: {
  icon: React.ElementType;
  value: string | number;
  unit?: string;
  dim?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono border ${
        dim
          ? 'border-[var(--border-color)] text-[var(--text-muted)] bg-transparent'
          : 'border-[var(--border-focus)] text-[var(--text-secondary)] bg-[rgba(58,95,138,0.06)]'
      }`}
    >
      <Icon size={10} strokeWidth={1.8} className={dim ? 'text-[var(--text-muted)]' : 'text-[var(--accent)]'} />
      <span className={dim ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]'}>{value}</span>
      {unit && <span className="text-[var(--text-muted)]">{unit}</span>}
    </span>
  );
}

/* ─── Rack bento card ───────────────────────────────────────────────────────── */

function RackCard({ rack, dcCode, size = 'md' }: {
  rack: RackConfig;
  dcCode: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const title = rack.label || formatRackName(undefined, undefined, rack.row_index, rack.rack_index);
  const subtitle = rack.name ? `Ряд ${rack.row_index + 1}, Позиция ${rack.rack_index + 1}` : null;
  const isBlocked = rack.is_blocked;

  return (
    <article
      className={`group relative flex flex-col rounded-sm border bg-[var(--bg-panel)] overflow-hidden
        transition-all duration-200 ease-out
        hover:border-[var(--border-focus)] hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(58,95,138,0.18)]
        focus-within:border-[var(--border-focus)] focus-within:ring-1 focus-within:ring-[var(--border-focus)]
        ${isBlocked
          ? 'border-[rgba(139,92,246,0.3)] opacity-80'
          : 'border-[var(--border-color)]'
        }
        ${size === 'lg' ? 'p-5' : size === 'sm' ? 'p-3' : 'p-4'}
      `}
    >
      {/* Blocked overlay band */}
      {isBlocked && (
        <span className="absolute top-0 left-0 right-0 h-0.5 bg-[var(--status-blocked)]" aria-hidden />
      )}

      {/* Card top: title + status */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <Link
          to={`/${dcCode}/racks/${rack.id}`}
          className="group/link min-w-0 flex-1 focus:outline-none"
          aria-label={`Открыть стойку: ${title}`}
        >
          <h2
            className={`font-semibold leading-tight text-[var(--text-primary)] group-hover/link:text-[var(--accent)] transition-colors
              ${size === 'lg' ? 'text-base' : 'text-sm'}
            `}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">{subtitle}</p>
          )}
        </Link>

        <span
          className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm font-medium border
            ${isBlocked
              ? 'border-[rgba(139,92,246,0.4)] text-[var(--status-blocked)] bg-[rgba(139,92,246,0.08)]'
              : 'border-[rgba(16,185,129,0.3)] text-[var(--status-normal)] bg-[rgba(16,185,129,0.07)]'
            }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${isBlocked ? 'bg-[var(--status-blocked)]' : 'bg-[var(--status-normal)]'}`} />
          {isBlocked ? 'Заблокирована' : 'Активна'}
        </span>
      </div>

      {/* Spacer — pushes chips to bottom */}
      <div className="flex-1" />

      {/* Stat chips row */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <StatChip
          icon={Server}
          value={rack.is_facility || ['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'].includes(rack.type) ? '0' : (rack.u_slots ?? 42)}
          unit="U"
          dim={Boolean(rack.is_facility || ['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'].includes(rack.type))}
        />
        {rack.max_weight_kg ? (
          <StatChip icon={Weight} value={rack.max_weight_kg} unit="кг" />
        ) : (
          <StatChip icon={Weight} value="—" dim />
        )}
        {rack.max_power_w ? (
          <StatChip icon={Zap} value={rack.max_power_w} unit="Вт" />
        ) : (
          <StatChip icon={Zap} value="—" dim />
        )}
      </div>

      {/* Footer: map link */}
      <div className="mt-3 pt-3 border-t border-[var(--border-color)] flex items-center justify-between">
        <span className="text-[10px] font-mono text-[var(--text-muted)]">
          #{rack.id}
        </span>
        <Link
          to={`/${dcCode}/calculator?highlight=${rack.id}`}
          className="inline-flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border-focus)] rounded-sm"
          aria-label={`Показать стойку ${title} на карте`}
        >
          <Map size={10} />
          На карте
        </Link>
      </div>
    </article>
  );
}

/* ─── Skeleton card ─────────────────────────────────────────────────────────── */

function RackCardSkeleton({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  return (
    <div
      className={`rounded-sm border border-[var(--border-color)] bg-[var(--bg-panel)] flex flex-col
        ${size === 'lg' ? 'p-5' : size === 'sm' ? 'p-3' : 'p-4'}
      `}
      aria-hidden
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 w-28 skeleton rounded-sm" />
          <div className="h-2.5 w-20 skeleton rounded-sm" />
        </div>
        <div className="h-5 w-20 skeleton rounded-sm" />
      </div>
      <div className="flex-1" />
      <div className="flex gap-1.5 mt-2">
        <div className="h-4 w-12 skeleton rounded-sm" />
        <div className="h-4 w-14 skeleton rounded-sm" />
        <div className="h-4 w-16 skeleton rounded-sm" />
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--border-color)] flex justify-between">
        <div className="h-3 w-6 skeleton rounded-sm" />
        <div className="h-3 w-14 skeleton rounded-sm" />
      </div>
    </div>
  );
}

/* ─── Summary stats bar ─────────────────────────────────────────────────────── */

function SummaryBar({ racks }: { racks: RackConfig[] }) {
  const itRacks = racks.filter(r => !r.is_facility && !['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'].includes(r.type));
  const total = itRacks.length;
  const active = itRacks.filter(r => !r.is_blocked).length;
  const blocked = itRacks.filter(r => r.is_blocked).length;
  const totalU = itRacks.reduce((s, r) => s + (r.u_slots ?? 42), 0);
  const totalPowerKw = itRacks.reduce((s, r) => s + (r.max_power_w ?? 0), 0) / 1000;

  const stats = [
    { label: 'Всего стоек', value: total, mono: false },
    { label: 'Активных', value: active, mono: false, highlight: true },
    { label: 'Заблокировано', value: blocked, mono: false, dim: blocked === 0 },
    { label: 'Юнитов (суммарно)', value: `${totalU}U`, mono: true },
    { label: 'Мощность (макс.)', value: totalPowerKw > 0 ? `${totalPowerKw.toFixed(1)} кВт` : '—', mono: true },
  ];

  return (
    <div className="flex flex-wrap gap-px border border-[var(--border-color)] rounded-sm overflow-hidden mb-6">
      {stats.map((s, i) => (
        <div
          key={i}
          className="flex-1 min-w-[110px] px-4 py-2.5 bg-[var(--bg-panel)] flex flex-col gap-0.5"
        >
          <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-medium">{s.label}</span>
          <span
            className={`text-lg font-bold leading-tight ${
              s.mono ? 'font-mono text-[var(--text-secondary)]' :
              s.highlight ? 'text-[var(--status-normal)]' :
              s.dim ? 'text-[var(--text-muted)]' :
              'text-[var(--text-primary)]'
            }`}
          >
            {s.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────────── */

export default function RacksListPage() {
  const { dcCode } = useParams<{ dcCode: string }>();
  const { activeWorkspaceId } = useWorkspace();
  const [racks, setRacks] = useState<RackConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dcCode) {
      setRacks([]);
      return;
    }
    setLoading(true);
    setError(null);
    api.get(`/api/datacenters/${dcCode}/racks`, {
      params: { workspace: activeWorkspaceId }
    })
      .then(res => setRacks(res.data))
      .catch(() => setError('Не удалось загрузить стойки. Проверьте соединение.'))
      .finally(() => setLoading(false));
  }, [dcCode, activeWorkspaceId]);

  /* Bento layout: first rack is 'lg', second is 'lg', rest are 'md' or 'sm' */
  const sizeFor = (i: number): 'lg' | 'md' | 'sm' =>
    i < 2 ? 'lg' : i < 6 ? 'md' : 'sm';

  const skeletonSizes: Array<'lg' | 'md' | 'sm'> = [
    'lg', 'lg', 'md', 'md', 'md', 'md', 'sm', 'sm', 'sm',
  ];

  return (
    <div className="h-full flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)]">

      {/* ── Page header ── */}
      <header className="px-6 py-4 border-b border-[var(--border-color)] flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2.5">
            <LayoutGrid size={20} className="text-[var(--accent)]" aria-hidden />
            Стойки
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            Реестр стоечного оборудования ЦОД
          </p>
        </div>

        {dcCode && (
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <Link
              to={`/${dcCode}/floor-plan`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] bg-[var(--bg-panel)] transition-colors font-medium"
              title="Открыть план зала"
            >
              <LayoutGrid size={14} className="text-[var(--accent)]" />
              План зала
            </Link>
            <span className="px-2 py-1 rounded-sm border border-[var(--border-color)] font-mono text-[var(--text-secondary)] bg-[var(--bg-panel)]">
              {dcCode}
            </span>
          </div>
        )}
      </header>

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto">
        {!dcCode ? (
          /* No DC selected */
          <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--text-muted)]">
            <Database size={40} strokeWidth={1} className="opacity-30" />
            <p className="text-sm">Выберите ЦОД в меню навигации, чтобы просмотреть стойки.</p>
          </div>
        ) : error ? (
          /* Error state */
          <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--status-critical)]">
            <AlertCircle size={36} strokeWidth={1.5} className="opacity-60" />
            <p className="text-sm text-center max-w-xs">{error}</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Summary bar */}
            {loading
              ? <div className="h-14 skeleton rounded-sm mb-6" />
              : racks.length > 0 && <SummaryBar racks={racks} />
            }

            {/* Bento grid */}
            {loading ? (
              /* Skeleton grid */
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gridAutoRows: 'auto',
                }}
                aria-busy="true"
                aria-label="Загрузка стоек…"
              >
                {skeletonSizes.map((sz, i) => (
                  <div
                    key={i}
                    style={sz === 'lg' && i < 2 ? { gridColumn: 'span 2' } : undefined}
                    className={sz === 'lg' && i < 2 ? 'min-[540px]:col-span-2' : ''}
                  >
                    <RackCardSkeleton size={sz} />
                  </div>
                ))}
              </div>
            ) : racks.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-[var(--text-muted)]">
                <div
                  className="w-16 h-16 rounded-sm border border-[var(--border-color)] flex items-center justify-center bg-[var(--bg-panel)]"
                  aria-hidden
                >
                  <Database size={28} strokeWidth={1} className="opacity-40" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-[var(--text-secondary)]">Стоек пока нет</p>
                  <p className="text-xs mt-1">
                    Добавьте стойки через{' '}
                    <Link
                      to={`/${dcCode}/calculator`}
                      className="text-[var(--accent)] hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border-focus)] rounded-sm"
                    >
                      2D Редактор
                    </Link>
                  </p>
                </div>
              </div>
            ) : (
              /* Bento grid — racks */
              <div
                role="list"
                aria-label={`Стойки ЦОД ${dcCode}`}
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
              >
                {racks.map((rack, i) => {
                  const sz = sizeFor(i);
                  return (
                    <div
                      key={rack.id}
                      role="listitem"
                      className={sz === 'lg' ? 'min-[480px]:col-span-2' : ''}
                    >
                      <RackCard rack={rack} dcCode={dcCode} size={sz} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
